import { useState } from 'react';
import { toast } from 'sonner';
import { useFetch, Panel, Tag } from '@/components/Common';
import { api, errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function Custodian() {
  const { data, error, reload } = useFetch('/command/custodian/status', [], 10000);
  const [key, setKey] = useState(''); const [busy,setBusy] = useState(false);
  const copyKey = async () => {
    if (!key) return;
    try {
      await navigator.clipboard.writeText(key);
      toast.success('Custodian key copied. Paste it only into the Windows Custodian program.');
    } catch {
      toast.error('Clipboard unavailable. Open this panel in the cloud Windows browser and try Copy Custodian key there.');
    }
  };
  const pair = async () => {
    setBusy(true);
    try { const r = await api.post('/command/custodian/pair'); setKey(r.data.key); reload(); }
    catch(e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const job = async action => {
    setBusy(true);
    try { await api.post('/command/custodian/jobs',{id:crypto.randomUUID(),action,payload:{}}); reload(); toast.success('Maintenance task queued'); }
    catch(e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.post('/command/custodian/revoke'); setKey(''); reload(); }
    catch(e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <Panel title="NEXUS Custodian · Windows worker" right={<Tag tone={data?.connected?'blue':'warn'}>{data?.connected?'Connected':'Not connected'}</Tag>}>
    <p className="text-sm text-muted-foreground">Separate cloud-Windows maintenance channel. Source staging, syntax validation, backups and rollback only. It does not execute or deploy generated code, restart services, control MT5, or repair the live web app. These capabilities must not be inferred from a connected heartbeat.</p>
    {error && <p className="mt-2 text-sm text-destructive" role="alert">{errMsg(error)}</p>}
    <div className="mt-4 flex flex-wrap gap-2">
      <Button onClick={pair} disabled={busy}>{data?.paired?'Replace Custodian key (disconnects old worker)':'Create separate Custodian key'}</Button>
      <a href={`${api.defaults.baseURL}/command/custodian/download.zip`} className="inline-flex items-center rounded border border-border px-4 py-2 text-sm">Download Windows worker</a>
      {data?.paired && <Button variant="outline" onClick={revoke} disabled={busy}>Revoke worker access</Button>}
    </div>
    {key && <div className="mt-4 space-y-2 rounded border border-border p-3">
      <p className="text-xs">One-time key. Copy manually into the Custodian Windows program. Never paste OpenAI or trading keys there.</p>
      <Input type="password" readOnly value={key} aria-label="One-time Custodian pairing key" autoComplete="off" />
      <Button variant="outline" onClick={copyKey}>Copy Custodian key</Button>
      <p className="text-xs">Copy and paste inside the same cloud Windows desktop. Your laptop and the cloud computer have separate clipboards.</p>
      <Button variant="outline" onClick={()=>setKey('')}>Hide key after copying</Button>
    </div>}
    <div className="mt-4 flex flex-wrap gap-2">
      <Button variant="outline" disabled={busy||!data?.connected} onClick={()=>job('health')}>Measure cloud host</Button>
      <Button variant="outline" disabled={busy||!data?.connected} onClick={()=>job('validate')}>Check staged source syntax</Button>
      <Button variant="outline" disabled={busy||!data?.connected} onClick={()=>job('self_test')}>Run backup / rollback self-test</Button>
      <Button variant="outline" onClick={reload}>Refresh worker status</Button>
    </div>
    {data?.health && <p className="mt-3 font-mono text-xs">RAM {data.health.ram_total_mb ?? '—'} MB · Free RAM {data.health.ram_free_mb ?? '—'} MB · Free disk {data.health.disk_free_mb ?? '—'} MB · CPUs {data.health.cpu_count ?? '—'}</p>}
    <p className="mt-3 text-xs text-muted-foreground">Keys are revocable and do not expire every 24 hours. Optional Windows sign-in startup is not an unattended boot service. No paid model calls are made by these maintenance checks.</p>
    <div className="mt-4 space-y-2">{data?.jobs?.map(j=><div key={j.id} className="rounded border border-border p-3 text-xs"><span className="font-mono">{j.action} · {j.status} · {j.id}</span>{j.result?.code && <p>{j.result.code}</p>}{j.result?.rollback_verified && <p>Worker reports backup and rollback verified. No live code deployed.</p>}</div>)}</div>
  </Panel>;
}
